import { useState, useEffect, useCallback } from 'react';
import { doctorService } from '../services/doctorService';
import { DoctorFormData } from '../types';

const initialFormData: DoctorFormData = {
  first_name: '',
  last_name: '',
  username: '',
  email: '',
  password: '',
  specialty_id: 0,
  phone: '',
  mobile: '',
  whatsapp: '',
  extension: '',
  date_of_birth: '',
  gender: undefined,
  education: '',
  designation: '',
  address: '',
  city: '',
  country: '',
  license_number: '',
  professional_rnc: '',
  bills_independently: false,
  consultation_fee: undefined,
  notes: '',
  is_active: true,
  is_available_for_appointments: true,
};

export const useDoctorForm = (doctorId?: number) => {
  const [formData, setFormData] = useState<DoctorFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!doctorId);

  useEffect(() => {
    if (!doctorId) {
      setInitialLoading(false);
      return;
    }

    const loadDoctor = async () => {
      setInitialLoading(true);
      try {
        const doctor = await doctorService.getById(doctorId);
        setFormData({
          first_name: doctor.first_name || '',
          last_name: doctor.last_name || '',
          username: doctor.username || '',
          email: doctor.email || '',
          password: '', // Don't load password for security
          specialty_id: doctor.specialty_id || 0,
          phone: doctor.phone || '',
          mobile: doctor.mobile || '',
          whatsapp: doctor.whatsapp || '',
          extension: doctor.extension || '',
          date_of_birth: doctor.date_of_birth || '',
          gender: doctor.gender as 'male' | 'female' | 'other' | undefined,
          education: doctor.education || '',
          designation: doctor.designation || '',
          address: doctor.address || '',
          city: doctor.city || '',
          country: doctor.country || '',
          license_number: doctor.license_number || '',
          professional_rnc: doctor.professional_rnc || '',
          bills_independently: doctor.bills_independently ?? false,
          consultation_fee: doctor.consultation_fee,
          notes: doctor.notes || '',
          is_active: doctor.is_active ?? true,
          is_available_for_appointments: doctor.is_available_for_appointments ?? true,
        });
      } catch (err) {
        console.error('Error loading doctor:', err);
      } finally {
        setInitialLoading(false);
      }
    };

    loadDoctor();
  }, [doctorId]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) newErrors.first_name = 'El nombre es requerido';
    if (!formData.last_name.trim()) newErrors.last_name = 'El apellido es requerido';
    if (!formData.specialty_id) newErrors.specialty_id = 'La especialidad es requerida';

    // Validate email if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El correo electrónico no es válido';
    }

    // Validate professional RNC if provided
    if (formData.professional_rnc) {
      const digits = formData.professional_rnc.replace(/\D/g, '');
      if (!/^\d{9}$|^\d{11}$/.test(digits)) {
        newErrors.professional_rnc = 'El RNC debe tener 9 u 11 dígitos numéricos';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      // Prepare data - remove empty password on update
      const dataToSend = { ...formData };
      if (doctorId && !dataToSend.password) {
        delete dataToSend.password;
      }

      if (doctorId) {
        await doctorService.update(doctorId, dataToSend);
        alert('Médico actualizado exitosamente');
      } else {
        const response = await doctorService.create(dataToSend);

        // Show credentials info if available
        if (response.credentials) {
          const { username, temporary_password, email_sent } = response.credentials;
          const message = email_sent
            ? `Médico creado exitosamente. Las credenciales han sido enviadas al correo del médico.\n\nUsuario: ${username}\nContraseña temporal: ${temporary_password}`
            : `Médico creado exitosamente.\n\nUsuario: ${username}\nContraseña temporal: ${temporary_password}\n\nNota: No se pudo enviar el correo electrónico.`;

          alert(message);
        } else {
          alert('Médico creado exitosamente');
        }
      }
      return true;
    } catch (error: any) {
      console.error('Error saving doctor:', error);
      alert(error?.message || 'Error al guardar médico');
      return false;
    } finally {
      setSaving(false);
    }
  }, [formData, doctorId, validateForm]);

  const handleInputChange = useCallback((field: keyof DoctorFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
  }, []);

  return {
    formData,
    errors,
    loading: initialLoading,
    saving,
    handleSubmit,
    handleInputChange,
    resetForm,
    setFormData,
  };
};
